import { Check } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import { type PersonnelDetail, type PersonnelTraining } from '../personnelTypes';

/**
 * Training/L&D — CS Form 212, section VII.
 *
 * Captured from the hospital system's Trainings module. Its type, category, intervention and
 * sponsor are CATALOG rows as of 15 Sep 2026 — the names below are resolved from those
 * vocabularies on every read, so correcting one corrects every record naming it.
 *
 * **The competency-development columns are gone with the form that filled them.** Purpose, the
 * competencies addressed and the two levels are no longer collected, so a column for each would
 * be four em dashes on every row — which says nothing and costs the width the record's own
 * facts need. The values are still on the record and still on the API; restoring the block
 * means restoring these columns too.
 *
 * **Most recent first**, matching the two histories above it — a reader moving between Work
 * experience, Voluntary work and this must not find them running in opposite directions.
 */
export function TrainingPanel({
  record,
  onAdd,
  onEdit,
  onDelete,
  onBulkDelete,
  onRestore,
}: {
  record: PersonnelDetail;
  onAdd: () => void;
  onEdit: (row: PersonnelTraining) => void;
  onDelete: (row: PersonnelTraining) => void;
  onBulkDelete: (rows: PersonnelTraining[]) => void;
  onRestore: (row: PersonnelTraining) => void;
}) {

  const [showDeleted, setShowDeleted] = useState(false);

  const rows = useMemo(
    () => (showDeleted ? record.trainings : record.trainings.filter((x) => x.isActive)),
    [record.trainings, showDeleted],
  );

  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? rows.filter((x) =>
            [
              x.recordNumber,
              x.title,
              /*
                The resolved NAMES, not ids — "which of these were run by the academy" is a
                question about a word the reader can see, and a Guid matches nothing anybody
                would type. Searching the vocabularies is what replaces searching the sponsor
                text column this panel used to read.
              */
              x.typeName,
              x.categoryName,
              x.interventionName,
              x.sponsorName,
            ]
              .filter(Boolean)
              .some((value) => value!.toLowerCase().includes(term)),
          )
        : rows;

      const field = query.sort?.replace(/^-/, '') ?? 'dateFrom';
      const descending = query.sort?.startsWith('-') ?? false;
      const direction = descending ? -1 : 1;

      const sorted = [...matched].sort((a, b) => {
        if (field === 'numberOfHours') {
          /*
            A NUMBER, so it compares as one — string order would put 9 above 40. Rows with no
            recorded hours sort last whichever way the column is pointed: null here means nobody
            counted them, and burying those among the totals would read as zero hours attended,
            which is a different answer the column can also hold.
          */
          const av = a.numberOfHours ?? null;
          const bv = b.numberOfHours ?? null;
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          return (av - bv) * direction;
        }

        if (field === 'dateFrom' || field === 'dateTo') {
          /*
            Both dates are NULLABLE on this record, unlike on the two histories above — the
            hospital's model carries plenty of rows where only the title and the sponsor
            survived. An undated row sorts LAST whichever way the column points: a missing date
            is an unrecorded one, and floating it to the top would read as the undated records
            being the most recent.
          */
          const av = (field === 'dateFrom' ? a.dateFrom : a.dateTo) ?? null;
          const bv = (field === 'dateFrom' ? b.dateFrom : b.dateTo) ?? null;
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          return av.localeCompare(bv) * direction;
        }

        const pick = (row: PersonnelTraining) =>
          field === 'recordNumber'
            ? row.recordNumber
            : field === 'sponsorName'
              ? (row.sponsorName ?? '')
              : field === 'typeName'
                ? row.typeName
                : field === 'categoryName'
                  ? row.categoryName
                  : row.title;

        return String(pick(a)).localeCompare(String(pick(b))) * direction;
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
      });
    },
    [rows],
  );

  const state = useDataTable<PersonnelTraining>({
    fetchPage,
    pageSize: 10,

    // Most recent first, matching the order the server sends them in and the two histories
    // above it.
    defaultSort: '-dateFrom',
  });

  const columns: DataColumn<PersonnelTraining>[] = [
    {
      /*
        The TITLE is the anchor, not the record number. A course is referred to by its name —
        "did she do the procurement one" — and a column of LND-2026-000NN titles every row
        identically to anyone who has not memorised the sequence. The number keeps its own
        column, where it can be searched and quoted.
      */
      key: 'title',
      header: 'Title',
      width: 280,
      sortable: true,
      render: (x) => <TextCell value={x.title} />,
    },
    {
      // A code, so mono. Server-assigned and permanent — it is what a certificate quotes.
      key: 'recordNumber',
      header: 'L&D no.',
      width: 150,
      sortable: true,
      render: (x) => <TextCell value={x.recordNumber} mono />,
    },
    {
      /*
        The NAME resolved from the catalog, never a stored copy — so rewording an L&D type
        rewords it here. Sorted and searched on that same resolved name, which is the value the
        reader can actually see; the id is the form's business.
      */
      key: 'typeName',
      header: 'L&D type',
      width: 180,
      sortable: true,
      summary: true,
      render: (x) => <TextCell value={x.typeName} />,
    },
    {
      key: 'categoryName',
      header: 'L&D category',
      width: 180,
      sortable: true,
      summary: true,
      render: (x) => <TextCell value={x.categoryName} />,
    },
    {
      key: 'interventionName',
      // The form's own words, not a coined shortening — "Delivery" said the same thing in a
      // word the reader has never seen on the form they filled in.
      header: 'Development intervention',
      width: 200,
      render: (x) => <TextCell value={x.interventionName} />,
    },
    {
      key: 'sponsorName',
      header: 'Conducted / sponsored by',
      width: 220,
      sortable: true,
      summary: true,
      render: (x) => <TextCell value={x.sponsorName} />,
    },
    {
      // Calendar dates — the day a course ran, not an instant.
      key: 'dateFrom',
      header: 'From',
      width: 130,
      sortable: true,
      render: (x) => <TextCell value={x.dateFrom ? formatDateOnly(x.dateFrom) : null} mono />,
    },
    {
      key: 'dateTo',
      header: 'To',
      width: 130,
      sortable: true,
      /*
        An em dash, NOT "Present" — and that is the opposite call from the two histories above.
        There a null end date is the form's own PRESENT, a real answer; here both dates are
        optional and a missing one usually means nobody wrote it down. Rendering "Present" would
        claim a course is still running when the truth is that its dates were never recorded.
      */
      render: (x) => <TextCell value={x.dateTo ? formatDateOnly(x.dateTo) : null} mono />,
    },
    {
      key: 'numberOfHours',
      header: 'Hours',
      width: 110,
      align: 'right',
      sortable: true,
      /*
        `numeric` as well as `mono`: hours are a quantity, so a zero dims like every other zero
        in the product — and a zero here is a real answer (enrolled, attended none) rather than
        an absence. An uncounted total is null and renders as an em dash.
      */
      render: (x) => (
        <TextCell
          value={x.numberOfHours === null || x.numberOfHours === undefined ? null : String(x.numberOfHours)}
          mono
          numeric
        />
      ),
    },
    {
      /*
        A panel whose rows can be deleted owes a status column: without one a deleted row carries
        no marker while Show deleted is on, which is the one state a row must never be silent
        about. `<DataTablePanel>` moves it to the end of the data columns itself.
      */
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (x) =>
        x.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Deleted</StatusText>
        ),
    },
  ];

  return (
    <ProfileSection>
      <DataTablePanel<PersonnelTraining>
        id="personnel-training"
        state={state}
        columns={columns}
        rowKey={(x) => x.id}
        searchPlaceholder="Search title, number, type, category or sponsor"
        onCreate={onAdd}
        createPermission="personnel.update"
        createDisabledReason={
          record.isActive
            ? null
            : 'This employee is deleted. Restore them before editing the record.'
        }
        /*
          The row opens as a key/value preview built from these same columns — ten of them is a
          horizontal scroll, and a scroll is a poor way to read ONE record.
        */
        // The size of the modal that EDITS this record, so reading a row and correcting it
        // happen at one width rather than the dialog changing shape between them.
        rowPreview={{ title: 'L&D record', size: '2xl' }}
        onEdit={(x) => onEdit(x)}
        editPermission="personnel.update"
        onDelete={(x) => onDelete(x)}
        deletePermission="personnel.update"
        rowActions={(x) =>
          x.isActive
            ? []
            : [
                {
                  key: `restore-${x.id}`,
                  label: 'Restore',
                  permission: 'personnel.update',
                  onClick: () => onRestore(x),
                },
              ]
        }
        otherInfo={(x) => ({
          createdAt: x.createdAt,
          createdByName: x.createdByName,
          updatedAt: x.updatedAt,
          updatedByName: x.updatedByName,
        })}
        /*
          The checkbox gutter is ON, which is the default — `selectable={false}` is only for a
          table nothing can ever be done to, never for one whose bulk action is merely unbuilt.
          So the bulk action ships with it.
        */
        bulkActions={(selected) => {
          const deletable = selected.filter((x) => x.isActive);

          return [
            {
              key: 'delete-trainings',
              label: `Delete ${deletable.length} record${deletable.length === 1 ? '' : 's'}`,
              permission: 'personnel.update',
              destructive: true,
              disabledReason: !record.isActive
                ? 'This employee is deleted. Restore them before editing the record.'
                : deletable.length === 0
                  ? 'Every selected record is already deleted.'
                  : null,
              onClick: () => onBulkDelete(deletable),
            },
          ];
        }}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            icon: showDeleted ? <Check className="h-4 w-4" aria-hidden="true" /> : undefined,
            onClick: () => setShowDeleted((current) => !current),
          },
        ]}
        emptyMessage="No L&D recorded yet."
        emptySearchMessage="No L&D record matches that search."
      />
    </ProfileSection>
  );
}
